import translate from '@translate';
import React, { FC, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import FormBlock from 'src/components/FormBlock';
import Box from '@mui/material/Box';
import Select from 'src/components/Select';
import TextField from 'src/components/TextField';
import Typography from 'src/components/Typography';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { UserType } from 'src/types/user';
import { langCodes } from 'src/resources/constantsCodes/constants';
import BodyTopBlock from 'src/components/BodyTopBlock';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import { updateUserFetch } from 'src/resources/user/reducer';
import Loading from 'src/components/Loading/intex';
import FileUpload, { FilesPropType } from 'src/components/FileUpload';
import { addMessage } from 'src/resources/alertMessages/reducer';
import { colors } from 'src/themes/default';
import { FormErrorsType } from '../../types/global';
import { TextFieldValueType } from 'src/components/TextField';
import UserNotificationsEditor, {
    UserNotificationsEditorHandle,
} from 'src/components/UserNotifications';
import {
    clearMyNotifications,
    getMyNotificationsFetch,
    updateMyNotificationsFetch,
} from 'src/resources/myNotifications/reducer';

export const EDIT_KEYS = ['NAME', 'SURNAME', 'PHONE', 'LANG', 'PHOTO_HASH', 'USER_EMAIL'];
type EditKeysType = 'NAME' | 'SURNAME' | 'PHONE' | 'LANG' | 'PHOTO_HASH' | 'USER_EMAIL';

const ProfilContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const formRef = useRef<HTMLFormElement>(null);
    const notificationsRef = useRef<UserNotificationsEditorHandle>(null);

    const data = useSelector((state: RootStoreType) => state.user.data);
    const loadingEdit = useSelector((state: RootStoreType) => state.user.loadingEdit);
    const myNotifications = useSelector((state: RootStoreType) => state.myNotifications.data);
    const loadingNotifications = useSelector((state: RootStoreType) => state.myNotifications.loading);

    const [editData, setEditData] = useState<UserType>(getEditData(data || {}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});

    const roleName = data?.roles?.[0]?.ROLE_NAME || '';

    useEffect(() => {
        dispatch(
            setLocationParams({
                name: translate('profil.title', {
                    userId: data?.USER_ID || '',
                    email: data?.USER_EMAIL || '',
                }),
            })
        );
    }, [dispatch, data?.USER_ID, data?.USER_EMAIL]);

    useEffect(() => {
        dispatch(getMyNotificationsFetch());
        return () => {
            dispatch(clearMyNotifications());
        };
    }, [dispatch]);

    const onChange = (key: EditKeysType, value: TextFieldValueType, error: boolean) => {
        setEditData((prev) => ({
            ...prev,
            [key]: value === undefined ? undefined : typeof value === 'number' ? value : String(value),
        }));
        setEditErrors((prev) => ({ ...prev, [key]: error }));
    };

    const onChangePhoto = (value: FilesPropType) => {
        setEditData((prev) => ({ ...prev, PHOTO_HASH: value[0]?.data || null }));
    };

    const changePassword = () => {
        navigate('/changePassword/');
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            dispatch(updateUserFetch(editData));
            const notificationsPayload = notificationsRef.current?.getPayload();
            if (notificationsPayload) {
                dispatch(updateMyNotificationsFetch(notificationsPayload));
            }
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText'),
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    return (
        <form ref={formRef}>
            {(loadingEdit || loadingNotifications) && <Loading />}
            <BodyTopBlock
                actions={[
                    { label: translate('profil.changePassword'), onClick: changePassword, variant: 'outlined' },
                    { label: translate('profil.save'), onClick: handleSave },
                ]}
            />

            <FormBlock>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
                        gap: 2,
                        alignItems: 'start',
                    }}
                >
                    <Box sx={{ gridRow: { md: 'span 2' } }}>
                        <Typography variant="h2" gutterBottom>
                            {translate('profil.profilePictureTitle')}
                        </Typography>
                        <FileUpload
                            files={editData.PHOTO_HASH ? [{ data: editData.PHOTO_HASH }] : undefined}
                            onChange={onChangePhoto}
                            accept="image/jpeg,image/jpg,image/png"
                            image
                            imageCircle
                        />
                    </Box>
                    <TextField
                        value={editData.NAME}
                        error={editErrors.NAME}
                        onChange={(value, error) => onChange('NAME', value, error)}
                        name="NAME"
                        label={translate('profil.name')}
                        fullWidth
                        required
                    />
                    <TextField
                        value={editData.SURNAME}
                        error={editErrors.SURNAME}
                        onChange={(value, error) => onChange('SURNAME', value, error)}
                        name="SURNAME"
                        label={translate('profil.surname')}
                        fullWidth
                        required
                    />
                    <TextField
                        value={editData.USER_EMAIL}
                        error={editErrors.USER_EMAIL}
                        name="USER_EMAIL"
                        label={translate('profil.userEmail')}
                        disabled
                        fullWidth
                    />
                    <TextField
                        value={editData.PHONE}
                        error={editErrors.PHONE}
                        onChange={(value, error) => onChange('PHONE', value, error)}
                        name="PHONE"
                        label={translate('profil.phone')}
                        fullWidth
                    />
                    <TextField
                        value={roleName}
                        name="USER_ROLE"
                        label={translate('profil.role')}
                        disabled
                        fullWidth
                    />
                    <Select
                        options={langCodes}
                        value={editData.LANG}
                        error={editErrors.LANG}
                        onChange={(value, error) => onChange('LANG', String(value), error)}
                        name="LANG"
                        label={translate('profil.language')}
                        required
                        fullWidth
                    />
                </Box>
            </FormBlock>

            <FormBlock sx={{ backgroundColor: colors.background_W }}>
                <Typography variant="h2" gutterBottom>
                    {translate('profil.notificationsTitle')}
                </Typography>
                <UserNotificationsEditor
                    ref={notificationsRef}
                    notifications={myNotifications}
                    permissions={data?.permissions}
                />
            </FormBlock>
        </form>
    );
};

export default ProfilContainer;
